import { useCallback, useEffect, useState } from 'react';

import { WalletOperation, fetchWalletOperation } from 'lib/models/walletOperations';

/**
 * One wallet operation, read by its id.
 *
 * The detail is fetched rather than carried over from the listing row, so an operation the
 * reconciler resolved while the list was on screen shows its current state when it is
 * opened instead of the state it had when the list was built.
 *
 * @param id The operation to read, or null while no operation is open
 */
function useWalletOperation(id: string | null) {
	const [loading, setLoading] = useState<boolean>(false);
	const [operation, setOperation] = useState<WalletOperation | null>(null);

	const refetch = useCallback(async () => {
		if (!id) {
			setOperation(null);
			return;
		}
		setLoading(true);
		const response = await fetchWalletOperation(id);
		setOperation(response);
		setLoading(false);
	}, [id]);

	/**
	 * Replaces the operation on screen with one the API just answered with.
	 *
	 * The three resolution endpoints answer with the whole record, so an action re-renders
	 * the detail from its own response — the state it left the operation in and the audit
	 * entry it just appended — instead of reading it back a second time.
	 */
	const update = useCallback((updated: WalletOperation) => {
		setOperation(updated);
	}, []);

	useEffect(() => {
		refetch();
	}, [refetch]);

	return { loading, operation, refetch, update };
}

export default useWalletOperation;
